import React, { Fragment, useEffect, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";
import {
  GetEstimates,
  DeleteEstimate
} from "../../../../api/medicalStore/transaction/estimateAPI";

const ListEstimateEntry = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  /* ================= FETCH DATA ================= */
  const fetchData = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetEstimates();

      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,

          vrType: item.vrType || "",
          vrNo: item.vrNo || "",
          date: item.date
            ? new Date(item.date).toLocaleDateString()
            : "",

          customerName: item.customerName || "-",
          doctor: item.doctorId?.name || "-",
          department: item.departmentId?.name || "-",

          grossAmount: item.grossAmount || 0,
          discountAmount: item.discountAmount || 0,
          roundOff: item.roundOff || 0,
          netAmount: item.netAmount || 0,

          createdBy: item.createdBy?.name || "-",
          createdAt: new Date(item.createdAt).toLocaleDateString()
        }));

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message || "Something went wrong");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ================= SEARCH ================= */
  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  /* ================= ACTIONS ================= */
  const handleEditClick = id => {
    navigate(
      `/medical-store/transaction/estimate-entry/edit/${id}`
    );
  };

  const handleDeleteClick = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will soft delete the estimate!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteEstimate(id);
          if (response.success) {
            showToast("success", response.message);
            setData(prev => prev.filter(item => item.id !== id));
            setFilteredData(prev => prev.filter(item => item.id !== id));
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  /* ================= TABLE COLUMNS ================= */
  const columns = [
    { name: "S.No", selector: row => row.index, width: "8%" },
    { name: "VR Type", selector: row => row.vrType, sortable: true, width: "10%" },
    { name: "VR No", selector: row => row.vrNo, sortable: true, width: "10%" },
    { name: "Date", selector: row => row.date, sortable: true, width: "10%" },
    { name: "Customer", selector: row => row.customerName, sortable: true, width: "10%" },
    { name: "Doctor", selector: row => row.doctor, sortable: true, width: "10%" },
    { name: "Department", selector: row => row.department, sortable: true, width: "10%" },
    { name: "Gross", selector: row => row.grossAmount, sortable: true, width: "10%" },
    { name: "Discount", selector: row => row.discountAmount, sortable: true, width: "10%" },
    { name: "Round Off", selector: row => row.roundOff, sortable: true, width: "10%" },
    { name: "Net Amount", selector: row => row.netAmount, sortable: true, width: "10%" },
    { name: "Created By", selector: row => row.createdBy, width: "10%" },
    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      button: true, width: "15%"
    }
  ];

  /* ================= CSV EXPORT ================= */
  const headers = [
    { label: "S.No", key: "index" },
    { label: "VR Type", key: "vrType" },
    { label: "VR No", key: "vrNo" },
    { label: "Date", key: "date" },
    { label: "Customer Name", key: "customerName" },
    { label: "Doctor", key: "doctor" },
    { label: "Department", key: "department" },
    { label: "Gross Amount", key: "grossAmount" },
    { label: "Discount Amount", key: "discountAmount" },
    { label: "Round Off", key: "roundOff" },
    { label: "Net Amount", key: "netAmount" },
    { label: "Created By", key: "createdBy" },
    { label: "Created At", key: "createdAt" }
  ];

  return (
    <Fragment>
      <Breadcrumb parent="Medical Store" title="Estimate List" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Estimate List</h5>
            <Link
              to={`/medical-store/transaction/estimate-entry/add`}
            >
              <button className="btn btn-primary">Create Estimate</button>
            </Link>
          </div>

          <div className="row p-3">
            <div className="col-md-6">
              <input
                className="form-control"
                placeholder="Search Estimate..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="col-md-6 text-end">
              <CSVLink
                data={data}
                headers={headers}
                filename="estimate-list.csv"
                className="btn btn-primary"
              >
                Export
              </CSVLink>
            </div>
          </div>

          <div className="card-body">
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
              dense
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListEstimateEntry;
